import {
  Component,
  ElementRef,
  inject,
  OnInit,
  OnDestroy,
  HostListener,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject, interval } from 'rxjs';
import { filter, takeUntil, timeout } from 'rxjs/operators';

import { QuizHeaderComponent } from '../../../ui/views/quiz-header/quiz-header.component';
import { QuizQuestionComponent } from '../../../ui/views/quiz-question/quiz-question.component';
import { ButtonComponent } from '../../../ui/button/button.component';
import { LoaderComponent } from '../../../ui/loader/loader.component';
import { Test, TestQuestion } from '../../../../core/interfaces/test';
import { TestService } from '../../../../core/services/test.service';
import { StudentTestService, SubmitAnswerItemDto } from '../../../../core/services/student-test.service';
import { SwalService } from '../../../../core/services/utils/swal.service';
import { TestTimerService, TestTimerState } from '../../../../core/services/utils/test-timer.service';
import { ExamSessionService } from '../../../../core/services/utils/exam-session.service';
import { PracticeContextService } from '../../../../core/services/practice-context.service';
import { PracticeQuestion } from '../../../../core/interfaces/practice';
import { instantUtc } from '../../../../core/utils/instant-utc';

/**
 * Réponses enregistrées par le serveur, regroupées par question comme l'écran
 * les tient. Une ligne sans option est une question vidée par l'élève : elle
 * ne compte pas pour répondue.
 */
export function reponsesDuServeur(
  reponses: { questionId: number; answerId: number | null }[] | null | undefined,
): Map<number, number[]> {
  const parQuestion = new Map<number, number[]>();
  for (const { questionId, answerId } of reponses ?? []) {
    if (answerId == null) continue;
    parQuestion.set(questionId, [...(parQuestion.get(questionId) ?? []), answerId]);
  }
  return parQuestion;
}

/**
 * Secondes qui restent avant l'heure de fin fixée par le serveur, ou `null`
 * pour une session sans limite. L'API écrit ses dates en UTC sans le dire.
 */
export function secondesAvantEcheance(
  deadline: string | null | undefined,
  maintenant: number = Date.now(),
): number | null {
  if (!deadline) return null;
  return Math.max(0, Math.floor((instantUtc(deadline) - maintenant) / 1000));
}

/**
 * Temps restant à l'ouverture de la copie, en secondes.
 *
 * <p>Le serveur fait foi : il compte lui-même ce qui reste
 * (`restantServeur`), sans passer par l'horloge du poste, souvent fausse sur
 * un poste d'établissement. L'écran n'annonce jamais davantage — le serveur
 * refuse toute réponse au-delà. C'est vrai aussi d'une copie reprise sur un
 * autre appareil sans aucune réponse enregistrée, que rien d'autre ne
 * distingue d'une copie neuve.</p>
 *
 * <p>Sans ce nombre (API antérieure), repli : l'échéance, lue avec l'horloge
 * du poste, et seulement pour une reprise sur un autre appareil.</p>
 *
 * @param tempsLocal décompte du navigateur, ajusté du temps écoulé, ou
 *        `null` sans mémoire de cette copie.
 * @param restantServeur secondes restantes selon le serveur ; `null` sans
 *        limite, absent d'une API qui ne le fournit pas.
 */
export function tempsRestantAOuverture({
  tempsLocal,
  reprise,
  tempsPrevu,
  avantEcheance,
  restantServeur,
}: {
  tempsLocal: number | null;
  reprise: boolean;
  tempsPrevu: number;
  avantEcheance: number | null;
  restantServeur?: number | null;
}): number {
  if (restantServeur != null) {
    const base = tempsLocal ?? tempsPrevu;
    return base > 0 ? Math.min(base, restantServeur) : restantServeur;
  }
  // Rechargement : le décompte local ne dépend que du temps écoulé.
  if (tempsLocal !== null) return tempsLocal;
  // Reprise ailleurs : seule l'échéance dit ce qui reste.
  if (reprise && avantEcheance !== null) {
    return tempsPrevu > 0 ? Math.min(tempsPrevu, avantEcheance) : avantEcheance;
  }
  // Copie neuve : tout son temps, quelle que soit l'heure du poste.
  return tempsPrevu;
}

/** Où reprendre : la première question sans réponse, ou la dernière. */
export function premiereQuestionSansReponse(
  questions: TestQuestion[],
  answers: Map<number, number[]>,
): number {
  const index = questions.findIndex((q) => !answers.get(q.question.id)?.length);
  return index >= 0 ? index : Math.max(questions.length - 1, 0);
}

/**
 * Ce qu'un battement d'horloge ajoute au temps passé sur la copie.
 *
 * <p><b>Pourquoi ce n'est plus « une seconde par battement ».</b> Le compteur
 * s'incrémentait à chaque tour d'un <c>interval(1000)</c>, et les navigateurs
 * <b>brident</b> ces minuteurs dès que l'onglet passe à l'arrière-plan — un
 * battement par minute, voire aucun quand le téléphone se verrouille. Une
 * séance ouverte à 14 h 29 et close à 15 h 40 s'est ainsi enregistrée à
 * 5 min 18 s. Le compteur ne mesurait pas le temps : il comptait les tours de
 * boucle que le navigateur avait bien voulu exécuter.</p>
 *
 * <p>L'horloge, elle, ne se bride pas : un battement en retard rapporte le
 * temps réellement écoulé depuis le précédent.</p>
 *
 * @param ecouleMs Temps écoulé depuis le battement précédent.
 * @param visible La copie est-elle à l'écran ? Le temps <b>passé sur le
 * test</b> exclut ce que l'élève a fait ailleurs — sans quoi une copie
 * abandonnée jusqu'à l'échéance annoncerait la durée entière de l'épreuve.
 * @returns Les secondes à ajouter, bornées : un poste mis en veille garde une
 * page « visible » et rendrait des heures au réveil.
 */
export function incrementDeTemps(ecouleMs: number, visible: boolean): number {
  if (!visible) return 0;
  return Math.min(Math.max(ecouleMs, 0) / 1000, PLAFOND_BATTEMENT_SECONDES);
}

/** Au-delà, un battement n'est plus un retard mais une interruption. */
const PLAFOND_BATTEMENT_SECONDES = 5;

/**
 * Les secondes pleines qu'un battement retire d'un compte à rebours, et le
 * reliquat reporté au battement suivant.
 *
 * <p><b>Pourquoi ce n'est plus « une seconde par battement ».</b> Même défaut
 * que le temps passé : les navigateurs brident les minuteurs d'un onglet en
 * arrière-plan. Le décompte gelait avec eux, et l'élève revenait devant un
 * minuteur qui lui annonçait dix minutes quand le serveur n'en accordait plus
 * aucune. Il était arrêté net, après avoir organisé sa fin d'épreuve sur un
 * chiffre faux.</p>
 *
 * <p><b>Aucun plafond, contrairement à {@link incrementDeTemps}, et aucune
 * condition de visibilité.</b> Ce sont deux grandeurs opposées : le temps passé
 * mesure le travail, il ne compte donc que la copie à l'écran ; le temps
 * restant mesure une <b>autorisation</b>, et l'échéance court qu'on la regarde
 * ou non — onglet en arrière-plan, écran verrouillé, poste en veille.</p>
 *
 * <p><b>Ce que cela ne fait pas :</b> comparer l'horloge du poste à l'échéance
 * du serveur. On ne lit ici qu'une <i>durée</i> locale, comme le fait déjà
 * <c>calculateAdjustedTimeRemaining</c> pour une copie reprise. Le garde-fou
 * contre les horloges de poste fausses — souvent décalées de plusieurs minutes
 * en établissement — reste donc entier : une horloge fausse mais régulière
 * mesure les durées aussi bien qu'une horloge juste.</p>
 *
 * <p>Le reliquat évite la dérive : arrondir chaque battement coûterait jusqu'à
 * une demi-seconde, mille huit cents fois sur une épreuve d'une heure.</p>
 */
export function secondesEcoulees(
  ecouleMs: number,
  reportMs = 0,
): { secondes: number; reportMs: number } {
  const total = Math.max(0, ecouleMs) + Math.max(0, reportMs);
  const secondes = Math.floor(total / 1000);
  return { secondes, reportMs: total - secondes * 1000 };
}

/**
 * Petit générateur pseudo-aléatoire déterministe (mulberry32).
 *
 * <p>Une même graine rend toujours la même suite. C'est ce qui permet à l'ordre
 * d'une copie d'être à la fois <b>tiré au sort</b> et <b>reproductible</b> —
 * sans être conservé nulle part, donc sans pouvoir être perdu.</p>
 */
function suiteDeterministe(graine: number): () => number {
  let etat = graine >>> 0;
  return () => {
    etat = (etat + 0x6d2b79f5) >>> 0;
    let t = etat;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * La graine d'une copie : sa séance, et éventuellement l'une de ses questions.
 *
 * <p>Deux élèves ne partagent jamais une séance, donc jamais un ordre. Deux
 * questions d'une même copie ne partagent pas non plus leur graine, sans quoi
 * leurs réponses se mélangeraient de la même façon — la bonne réponse
 * tomberait au même rang partout, ce qui est pire que pas de mélange.</p>
 */
export function graineDeSeance(sessionId: number, questionId = 0): number {
  return (
    (Math.imul(sessionId >>> 0, 0x9e3779b1) ^
      Math.imul((questionId >>> 0) + 1, 0x85ebca6b)) >>>
    0
  );
}

/**
 * Mélange reproductible : même graine, même ordre, toujours.
 *
 * <p><b>Pourquoi pas <c>Math.random()</c>.</b> L'ordre tiré n'était conservé
 * nulle part : à la reprise, l'écran rendait la copie dans l'ordre d'origine,
 * alors que la position, les questions visitées et surtout les questions
 * <b>expirées</b> sont mémorisées par leur <i>rang</i>. Un rechargement
 * décalait donc tout : l'élève reprenait à une autre question, et une question
 * jamais vue pouvait se retrouver marquée expirée — définitivement
 * inaccessible, et non notée.</p>
 *
 * <p>Le mélange était donc désactivé à la reprise pour contourner cela, ce qui
 * rendait l'ordre d'origine à qui rechargeait. Avec une graine, l'ordre se
 * <b>recalcule</b> à l'identique : rien à stocker, rien à perdre, et le même
 * ordre sur un autre appareil.</p>
 */
export function melangeAvecGraine<T>(items: readonly T[], graine: number): T[] {
  const melange = [...items];
  const tirage = suiteDeterministe(graine);

  // Fisher-Yates : chaque permutation équiprobable.
  for (let i = melange.length - 1; i > 0; i--) {
    const j = Math.floor(tirage() * (i + 1));
    [melange[i], melange[j]] = [melange[j], melange[i]];
  }

  return melange;
}

/**
 * Clé i18n expliquant pourquoi le test ne s'ouvre pas.
 *
 * <p>L'écran affichait `error.message` tel quel : « Http failure response for
 * …/sessions/start: 403 OK ». Le statut suffit à dire la raison, dans la
 * langue de l'élève — le texte technique reste dans la console.</p>
 */
export function cleRefusDemarrage(erreur: unknown): string {
  const statut = erreur instanceof HttpErrorResponse ? erreur.status : null;
  switch (statut) {
    case 403:
      return 'TESTS.START_REFUSED_NO_ATTEMPT';
    case 404:
      return 'TESTS.START_REFUSED_NOT_FOUND';
    case 406:
      return 'TESTS.START_REFUSED_UNAVAILABLE';
    case 0:
      return 'ERRORS.NETWORK';
    default:
      return 'ERRORS.LOAD_TEST_ERROR';
  }
}

interface TestSession {
  test: Test;
  questions: TestQuestion[];
  currentQuestionIndex: number;
  answers: Map<number, number[]>;
  startTime: Date;
  timeRemaining: number;
  /**
   * Faux quand le test n'a aucune limite (ni globale ni par question) :
   * timeRemaining vaut alors 0 en permanence et ne doit surtout pas être
   * lu comme « temps écoulé ».
   */
  hasTimeLimit: boolean;
  questionTimeRemaining: number;
  highestQuestionReached: number;
  visitedQuestions: Set<number>; // Track visited questions
  timedOutQuestions: Set<number>; // Track questions that timed out
  /**
   * Secondes réellement passées dans la passation : l'écran du test ouvert,
   * cumulées d'une ouverture et d'un appareil à l'autre. C'est la durée
   * remise avec la copie (SKILL2ERA-90) — et non le temps écoulé depuis son
   * ouverture, qui compte les heures où l'écran était fermé.
   */
  tempsActif: number;
}

@Component({
  selector: 'app-student-test-session',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    QuizHeaderComponent,
    QuizQuestionComponent,
    ButtonComponent,
    LoaderComponent,
  ],
  templateUrl: './student-test-session.component.html',
  styleUrl: './student-test-session.component.css',
})
export class StudentTestSessionComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private testService = inject(TestService);
  private studentTestService = inject(StudentTestService);
  translate = inject(TranslateService);
  private swal = inject(SwalService);
  private timerService = inject(TestTimerService);
  private examSessionService = inject(ExamSessionService);
  private practiceContext = inject(PracticeContextService);
  private destroy$ = new Subject<void>();

  testId: number | null = null;
  sessionId: number | null = null; // Backend session ID
  isLoading = true;
  hasError = false;
  errorMessage = '';
  isLTR = false;
  session: TestSession | null = null;

  // Timer tracking
  private testTimerSubscription?: any;
  private questionTimerSubscription?: any;
  private saveStateInterval?: any;

  // Public flags for guard
  isSubmitting = false;
  isExiting = false;
  /** Le temps imparti n'expire qu'une fois — voir startTestTimer(). */
  private timeoutTriggered = false;

  // ===== Behavior Tracking =====
  private questionStartTimestamp: number = 0;
  private questionTimeSpent = new Map<number, number>();

  /**
   * Questions que l'élève a marquées « à revoir », par identifiant.
   *
   * <p><b>Pourquoi.</b> Un élève qui hésite garde sa question en tête pendant
   * quarante minutes. Un marqueur libère cette charge : il note le doute et
   * avance, au lieu de le porter.</p>
   *
   * <p>Par identifiant et non par rang : les questions peuvent être mélangées,
   * et un rang ne désigne pas la même question d'une session à l'autre.</p>
   */
  marquees = new Set<number>();

  /** Le doute se voit sur la question courante. */
  get questionCouranteMarquee(): boolean {
    const id = this.currentQuestion?.question?.id;
    return id != null && this.marquees.has(id);
  }

  /** Reliquat de milliseconde du décompte de l'épreuve. */
  private reportEpreuve = 0;

  /** Reliquat de milliseconde du décompte de la question. */
  private reportQuestion = 0;

  /** Le navigateur de questions, borné en hauteur et défilant. */
  @ViewChild('navigateur') private navigateur?: ElementRef<HTMLElement>;

  /**
   * Bulles par page du navigateur de questions.
   *
   * <p><b>Pourquoi paginer.</b> Les diagnostics font 43 à 82 questions. Quatre-
   * vingt-deux bulles, c'est quatorze rangées sur un téléphone : un mur de
   * chiffres où l'œil ne trouve rien, et la moitié de l'écran prise par le
   * repère plutôt que par la question.</p>
   *
   * <p>Vingt tient en trois rangées et laisse cinq pages pour la plus longue
   * épreuve. Une taille <b>fixe</b>, non calculée sur la largeur : un élève qui
   * cherche la question 37 doit la trouver au même endroit à chaque fois, et
   * une page qui se recompose à la rotation de l'écran perd ce repère.</p>
   */
  readonly bullesParPage = 20;

  /** Page affichée du navigateur, à partir de 0. */
  pageNavigateur = 0;

  /**
   * Le repère de position est-il déployé ?
   *
   * <p><b>Sur téléphone seulement</b> — au-dessus, la grille ne coûte rien et
   * reste ouverte, la règle est dans la feuille de style. Sur un écran de
   * 568 px de haut, le repère et ses commandes prenaient 212 px : il ne
   * restait que 106 px à la question, moins de quatre lignes. Or la grille est
   * un outil <b>occasionnel</b> — revenir sur une question laissée de côté —
   * alors que l'énoncé se lit en permanence.</p>
   *
   * <p>Replié, le repère garde ce qui répond à « où en suis-je » : la position
   * sur le bouton qui le déploie, et le compte des réponses juste en dessous.</p>
   */
  repereOuvert = false;

  basculerRepere(): void {
    this.repereOuvert = !this.repereOuvert;
  }

  // ===== Anti-Cheat =====
  private tabSwitchCount = 0;
  private focusLossCount = 0;
  private fullscreenExitCount = 0;

  // Named handlers for proper removeEventListener
  private readonly onFullscreenChange = (): void => {
    if (!this.examSessionService.isExamActive) return;
    if (!document.fullscreenElement) {
      this.fullscreenExitCount++;
      this.saveBehavior();
      this.swal.warning(this.translate.instant('TESTS.FULLSCREEN_REQUIRED'));
      this.enterFullScreen();
    }
  };

  private readonly onVisibilityChange = (): void => {
    if (!this.examSessionService.isExamActive) return;
    if (document.hidden) {
      this.tabSwitchCount++;
      this.saveBehavior();
      this.swal.warning(this.translate.instant('TESTS.TAB_SWITCH_NOT_ALLOWED'));
      if (this.tabSwitchCount >= 5) {
        this.submitTest(true);
      }
    }
  };

  private readonly onWindowBlur = (): void => {
    if (!this.examSessionService.isExamActive) return;
    this.focusLossCount++;
    this.saveBehavior();
  };

  /**
   * La passation au clavier.
   *
   * <p><b>Pourquoi.</b> Un élève enchaîne cinquante questions. Viser une case
   * à la souris cinquante fois est lent, et fatigant — d'autant plus sur un
   * portable, au pavé tactile.</p>
   *
   * <p><b>Une touche pour ce qui se fait cinquante fois, une combinaison pour
   * ce qui s'engage une seule.</b> La remise est la seule à deux touches :
   * c'est la seule qui ne se rattrape pas.</p>
   *
   * <p><b>Les flèches suivent le sens de lecture.</b> En arabe, « suivant » va
   * vers la GAUCHE. Une flèche qui recule quand on croit avancer est pire que
   * pas de flèche du tout.</p>
   *
   * <p><kbd>Échap</kbd> n'a volontairement aucun rôle : elle sort du plein
   * écran, et cette sortie compte déjà comme un incident. Lui donner une
   * seconde signification rendrait l'incident illisible.</p>
   */
  @HostListener('document:keydown', ['$event'])
  auClavier(event: KeyboardEvent): void {
    if (!this.session || this.isSubmitting || this.isExiting) return;

    // La saisie libre garde ses touches : un « 2 » tapé dans une réponse
    // texte est un deux, pas un choix d'option.
    const cible = event.target as HTMLElement | null;
    if (cible?.closest('input, textarea, select, [contenteditable="true"]')) return;

    // Une boîte de dialogue OUVERTE a la main : répondre « oui » d'une flèche
    // pendant une confirmation serait une remise involontaire.
    //
    // `swal2-shown` sur le corps, et non la présence du conteneur : celui-ci
    // survit à la fermeture, et un dialogue clos n'a aucune raison de
    // confisquer le clavier pour le reste de l'épreuve.
    if (document.body.classList.contains('swal2-shown')) return;

    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      this.submitTest(false);
      return;
    }

    if (event.ctrlKey || event.metaKey || event.altKey) return;

    const avant = this.direction === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const arriere = this.direction === 'rtl' ? 'ArrowRight' : 'ArrowLeft';

    switch (event.key) {
      case avant:
      case 'Enter':
        event.preventDefault();
        this.goToNextQuestion();
        return;

      case arriere:
        event.preventDefault();
        this.goToPreviousQuestion();
        return;

      case ' ':
        event.preventDefault();
        this.basculerMarque();
        return;
    }

    const rang = Number.parseInt(event.key, 10);
    if (Number.isNaN(rang)) return;

    const options = this.currentQuestion?.question?.answerBanks ?? [];
    const option = options[rang - 1];
    if (!option) return;

    event.preventDefault();
    this.choisirParClavier(option.id);
  }

  /**
   * Le sens de lecture de l'épreuve, pour orienter les flèches.
   *
   * <p><c>isLTR</c> est déjà ce que l'écran utilise pour orienter son pied de
   * page : les flèches suivent donc exactement le sens dans lequel l'élève
   * voit sa copie, et non une seconde notion qui divergerait.</p>
   */
  private get direction(): 'rtl' | 'ltr' {
    return this.isLTR ? 'ltr' : 'rtl';
  }

  /**
   * Coche l'option désignée au clavier.
   *
   * <p>Passe par le même chemin que le clic — <c>selectAnswer</c> — pour que
   * le clavier ne soit pas une seconde implémentation qui dériverait : refus
   * sur question expirée, mise en file d'envoi, sauvegarde locale.</p>
   */
  private choisirParClavier(answerId: number): void {
    const question = this.currentQuestion;
    if (!question) return;

    const questionId = question.question?.id;
    if (questionId == null) return;

    const dejaLa = this.session?.answers.get(questionId) ?? [];
    const multiple = question.question?.questionType === 'multiple_choice';

    const suivant = multiple
      ? (dejaLa.includes(answerId)
          ? dejaLa.filter((id) => id !== answerId)
          : [...dejaLa, answerId])
      : [answerId];

    this.selectAnswer(questionId, suivant);
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    // Only save state and warn on refresh, not on navigation
    if (!this.isSubmitting && !this.isExiting && this.session) {
      this.saveCurrentState();

      // Show browser warning to prevent accidental refresh
      event.preventDefault();
      event.returnValue = this.translate.instant('TESTS.REFRESH_WARNING');
    }
  }

  ngOnInit(): void {
    this.isLTR = this.translate.currentLang !== 'ar';
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.isLTR = event.lang !== 'ar';
      });

    // End exam on any navigation away from this page
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationStart),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.endExamSession();
      });

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.testId = +params['id'];
      if (this.testId) {
        this.initializeTest();
      } else {
        this.handleError(new Error('Invalid test ID'));
      }
    });
  }

  private async initializeTest(): Promise<void> {
    try {
      this.isLoading = true;
      this.hasError = false;

      if (!this.testId) {
        throw new Error('Test ID is required');
      }

      console.log('🎯 Initializing test session:', this.testId);

      const ouverture = await this.ouvrirSession();
      if (ouverture === 'expiree') return;

      // Start timers
      this.startTestTimer();
      this.startQuestionTimer();
      this.startAutoSave();

      this.isLoading = false;
      console.log('✅ Test session initialized successfully');
      this.lockBodyScroll();
      this.startQuestionTracking();
      this.examSessionService.startExam();
      this.attachAntiCheatListeners();
      await this.enterFullScreen();

      // Après le plein écran, pas avant : la page en plein écran entre dans la
      // couche supérieure après l'alerte, et la recouvrait. L'élève ne voyait
      // jamais que sa copie avait été retrouvée.
      if (ouverture === 'reprise') {
        this.swal.info(
          this.translate.instant('TESTS.SESSION_RESTORED_MESSAGE'),
          this.translate.instant('TESTS.SESSION_RESTORED'),
        );
      } else if (ouverture === 'remplacee') {
        this.swal.warning(this.translate.instant('TESTS.PREVIOUS_ATTEMPT_CLOSED'));
      }
    } catch (error) {
      console.error('❌ Error initializing test:', error);
      this.handleError(error);
    }
  }

  /**
   * Ouvre la copie : celle que le serveur tient en cours, ou une neuve.
   *
   * <p>Le serveur fait foi (SKILL2ERA-84). `sessions/start` rend la session en
   * cours avec ses réponses et son heure de fin ; l'écran n'en lisait que le
   * numéro, et ne reprenait réellement que si ce navigateur-ci gardait une
   * trace du test. Sur un autre appareil, ou après un nettoyage du navigateur,
   * l'élève retrouvait une copie vide et un minuteur remis au temps complet.</p>
   *
   * <p>La mémoire du navigateur ne fait plus que compléter — position dans le
   * test, réponse dont l'envoi aurait échoué — et seulement pour la même
   * session : celle d'une tentative close depuis est écartée.</p>
   *
   * @returns `reprise` quand une copie a été retrouvée ; `remplacee` quand le
   *          navigateur gardait une tentative que le serveur a close depuis,
   *          et qu'une nouvelle commence ; `neuve` sinon ; `expiree` quand
   *          l'heure est passée : l'élève est alors renvoyé à la liste.
   */
  private async ouvrirSession(): Promise<'reprise' | 'remplacee' | 'neuve' | 'expiree'> {
    if (!this.testId) throw new Error('Test ID is required');
    const testId = this.testId;

    const memoire: TestTimerState | null = this.timerService.getTestState(testId);

    // Silencieuse : un refus (plus de tentative) s'explique ici, pas par une
    // redirection vers /unauthorized.
    const sessionResponse = await firstValueFrom(
      this.studentTestService.startTestSession({ testId }, { silencieuse: true }),
    );

    if (sessionResponse.status !== 'SUCCESS' || !sessionResponse.data) {
      throw new Error('Failed to start test session on server');
    }

    const serveur = sessionResponse.data;
    this.sessionId = serveur.id;

    // Endpoint élève (student-tests) : l'endpoint professeur renvoie 403 pour un élève
    // et cassait la reprise de session après un rafraîchissement en plein examen.
    const testResponse = await firstValueFrom(
      this.testService.getStudentTestWithQuestions(testId),
    );

    if (testResponse.status !== 'SUCCESS' || !testResponse.data) {
      throw new Error('Failed to load test details');
    }

    const test = testResponse.data;

    const locale = memoire?.sessionId === serveur.id ? memoire : null;
    if (memoire && !locale) {
      this.timerService.clearTestState(testId);
    }

    // Le serveur fait foi. Le navigateur ne rapporte que ce que le serveur n'a
    // pas reçu — envoi échoué ou encore en attente. Lui donner raison sur tout
    // ramenait une réponse périmée : A ici, changée pour B sur un autre
    // appareil, redevenait A au retour. Un état écrit avant l'enregistrement
    // en cours de test n'a rien envoyé : tout y est à reprendre.
    const answers = reponsesDuServeur(serveur.studentAnswers);
    const reponsesLocales = new Map<number, number[]>(locale?.answers ?? []);
    const aReprendre = locale
      ? locale.nonSynchronisees ?? Array.from(reponsesLocales.keys())
      : [];
    for (const questionId of aReprendre) {
      const answerIds = reponsesLocales.get(questionId) ?? [];
      if (answerIds.length) answers.set(questionId, answerIds);
      else answers.delete(questionId);
    }
    this.nonSynchronisees = new Set(aReprendre);
    this.marquees = new Set<number>((locale as { marquees?: number[] })?.marquees ?? []);

    const reprise = locale !== null || answers.size > 0;

    let questions = test.testQuestions || [];

    // L'ordre est propre à la copie, et il l'est toujours.
    //
    // Il ne dépend plus des drapeaux `shuffleQuestions` / `shuffleAnswers` :
    // vingt et un des quarante-quatre tests publiés les portaient à `false`
    // — la colonne vaut `false` par défaut, et l'écran d'import ne les
    // renseigne pas —, si bien que tous les élèves d'un même diagnostic
    // voyaient exactement la même suite de questions et de réponses. Le
    // brouillage est une règle de passation, pas une option de test.
    //
    // Il s'applique aussi à la reprise, ce qu'une graine rend possible : le
    // même ordre se recalcule au lieu d'être conservé. Voir
    // {@link melangeAvecGraine} pour ce que l'ancien mélange cassait.
    questions = melangeAvecGraine(questions, graineDeSeance(serveur.id));

    for (const question of questions) {
      const reponses = question.question?.answerBanks;
      if (reponses?.length) {
        question.question.answerBanks = melangeAvecGraine(
          reponses,
          graineDeSeance(serveur.id, question.question.id),
        );
      }
    }

    // La limite du test, et elle seule : c'est aussi celle dont le serveur
    // tire l'échéance. La somme des limites par question en tenait lieu dès
    // qu'une seule question en portait une — 60 s pour un diagnostic de
    // 60 minutes, remis d'office après la première question. Ces limites ne
    // règlent que le chronomètre de leur question (startQuestionTimer).
    const tempsPrevu = (test.timeLimitMinutes || 0) * 60;
    const avantEcheance = secondesAvantEcheance(serveur.deadline);
    const hasTimeLimit =
      tempsPrevu > 0 || avantEcheance !== null || serveur.secondsRemaining != null;

    // Un état sans limite porte timeRemaining = 0, qui ne doit pas être pris
    // pour une session expirée.
    let tempsLocal: number | null = null;
    if (locale) {
      tempsLocal = locale.hasTimeLimit === false
        ? tempsPrevu
        : this.timerService.calculateAdjustedTimeRemaining(locale);
    }
    const timeRemaining = tempsRestantAOuverture({
      tempsLocal,
      reprise,
      tempsPrevu,
      avantEcheance,
      restantServeur: serveur.secondsRemaining,
    });

    // Plus de temps : y compris pour une copie retrouvée sans aucune réponse,
    // que seul le temps du serveur distingue d'une copie neuve.
    if (hasTimeLimit && timeRemaining <= 0) {
      await this.swal.warning(this.translate.instant('TESTS.SESSION_EXPIRED'));
      this.timerService.clearTestState(testId);
      this.router.navigate(['/student/tests']);
      return 'expiree';
    }

    const currentQuestionIndex = locale
      ? Math.min(locale.currentQuestionIndex ?? 0, Math.max(questions.length - 1, 0))
      : premiereQuestionSansReponse(questions, answers);

    const visitedQuestions = new Set<number>(
      locale
        ? locale.visitedQuestions || []
        : Array.from({ length: currentQuestionIndex + 1 }, (_, i) => i),
    );
    visitedQuestions.add(currentQuestionIndex);

    let startTime = new Date();
    if (locale?.startTime) startTime = new Date(locale.startTime);
    else if (serveur.startedAt) startTime = new Date(instantUtc(serveur.startedAt));

    this.session = {
      test,
      questions,
      currentQuestionIndex,
      answers,
      startTime,
      timeRemaining,
      hasTimeLimit,
      questionTimeRemaining:
        questions[currentQuestionIndex]?.question?.timeLimitSeconds || 0,
      highestQuestionReached: Math.max(
        locale?.highestQuestionReached ?? 0,
        currentQuestionIndex,
      ),
      visitedQuestions,
      timedOutQuestions: new Set<number>(locale?.timedOutQuestions || []),
      // Le plus avancé des deux compteurs : celui de ce navigateur, ou celui
      // que le serveur a reçu d'un autre appareil.
      tempsActif: Math.max(locale?.tempsActif ?? 0, serveur.timeSpentSeconds ?? 0),
    };

    this.calerPageSurQuestionCourante();

    console.log(reprise ? '✅ Session restored:' : '🆕 Session started:', {
      sessionId: this.sessionId,
      answersCount: answers.size,
      currentQuestion: currentQuestionIndex + 1,
      timeRemaining,
    });

    // Ce que le serveur n'a jamais reçu repart dès maintenant.
    if (this.nonSynchronisees.size > 0) this.planifierEnregistrement();

    if (reprise) return 'reprise';
    return memoire ? 'remplacee' : 'neuve';
  }

  private startTestTimer(): void {
    let dernierBattement = Date.now();

    this.testTimerSubscription = interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const maintenant = Date.now();
        const ecouleMs = maintenant - dernierBattement;
        dernierBattement = maintenant;

        // Le temps passé se lit à l'horloge, et seulement quand la copie est à
        // l'écran. Voir {@link incrementDeTemps}.
        if (this.session) {
          this.session.tempsActif += incrementDeTemps(
            ecouleMs,
            document.visibilityState === 'visible',
          );
        }

        // Sans limite de temps, timeRemaining reste à 0 : aucune expiration.
        if (!this.session?.hasTimeLimit) return;

        // Ce que le battement a réellement duré, et non une seconde
        // forfaitaire. Voir {@link secondesEcoulees}.
        const decompte = secondesEcoulees(ecouleMs, this.reportEpreuve);
        this.reportEpreuve = decompte.reportMs;

        if (this.session.timeRemaining > 0) {
          this.session.timeRemaining = Math.max(
            0,
            this.session.timeRemaining - decompte.secondes,
          );
        }

        if (this.session.timeRemaining <= 0 && !this.timeoutTriggered) {
          // Déclenchement unique : le tick revient chaque seconde et, quand
          // la soumission échouait, le test était renvoyé en boucle au back
          // (une requête par seconde, jusqu'au bannissement anti-flood).
          this.timeoutTriggered = true;
          this.handleTestTimeout();
        }
      });
  }

  private startQuestionTimer(): void {
    let dernierBattement = Date.now();

    this.questionTimerSubscription = interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const maintenant = Date.now();
        const ecouleMs = maintenant - dernierBattement;
        dernierBattement = maintenant;

        if (!this.session || !this.currentQuestion) return;

        const limite = this.currentQuestion.question.timeLimitSeconds;
        if (!limite || limite <= 0) return;

        // Même règle que le décompte de l'épreuve : le temps mesuré, quel que
        // soit le rythme auquel le navigateur a bien voulu battre.
        const decompte = secondesEcoulees(ecouleMs, this.reportQuestion);
        this.reportQuestion = decompte.reportMs;

        if (this.session.questionTimeRemaining > 0) {
          this.session.questionTimeRemaining = Math.max(
            0,
            this.session.questionTimeRemaining - decompte.secondes,
          );
        }

        if (this.session.questionTimeRemaining <= 0) {
          this.handleQuestionTimeout();
        }
      });
  }

  private startAutoSave(): void {
    // Save state every 5 seconds
    this.saveStateInterval = setInterval(() => {
      if (!this.isSubmitting && !this.isExiting) {
        this.saveCurrentState();
      }
    }, 5000);
  }

  private saveCurrentState(): void {
    if (!this.session || !this.testId) return;

    const state = {
      testId: this.testId,
      sessionId: this.sessionId,
      startTime: this.session.startTime.getTime(),
      timeRemaining: this.session.timeRemaining,
      hasTimeLimit: this.session.hasTimeLimit,
      currentQuestionIndex: this.session.currentQuestionIndex,
      highestQuestionReached: this.session.highestQuestionReached,
      answers: Array.from(this.session.answers.entries()),
      visitedQuestions: Array.from(this.session.visitedQuestions),
      timedOutQuestions: Array.from(this.session.timedOutQuestions),
      nonSynchronisees: Array.from(this.nonSynchronisees),
      // Un rechargement ne doit pas effacer les doutes notés.
      marquees: Array.from(this.marquees),
      tempsActif: this.session.tempsActif,
    };

    this.timerService.saveTestState(this.testId, state);
  }

  private async handleTestTimeout(): Promise<void> {
    console.log('⏰ Test time expired - auto submitting...');

    await this.swal.warning(
      this.translate.instant('TESTS.TIME_EXPIRED_MESSAGE'),
    );

    await this.submitTest(true);
  }

  private async handleQuestionTimeout(): Promise<void> {
    if (!this.session) return;

    // Une seule fois par question. Le battement revient chaque seconde, et sur
    // la dernière question — qu'on ne quitte pas — l'alerte se rejouait
    // indéfiniment : l'élève ne pouvait plus rien faire de sa copie.
    if (this.session.timedOutQuestions.has(this.session.currentQuestionIndex)) {
      return;
    }

    console.log('⏰ Question time expired - marking and moving to next...');

    const currentQuestionId = this.currentQuestion?.question.id;
    if (currentQuestionId) {
      this.session.timedOutQuestions.add(this.session.currentQuestionIndex);
    }

    await this.swal.warning(
      this.translate.instant('TESTS.QUESTION_TIME_EXPIRED_MESSAGE'),
    );

    if (!this.isLastQuestion) {
      this.moveToNextQuestion();
    }
  }

  private moveToNextQuestion(): void {
    this.stopQuestionTracking();
    if (!this.session || this.isLastQuestion) return;

    this.session.currentQuestionIndex++;

    if (
      this.session.currentQuestionIndex > this.session.highestQuestionReached
    ) {
      this.session.highestQuestionReached = this.session.currentQuestionIndex;
    }

    this.session.visitedQuestions.add(this.session.currentQuestionIndex);

    const newQuestion = this.currentQuestion;
    if (newQuestion?.question.timeLimitSeconds) {
      this.session.questionTimeRemaining =
        newQuestion.question.timeLimitSeconds;
    }

    this.saveCurrentState();
    this.suivreLaQuestionCourante();
      this.startQuestionTracking(); // 🚀 redémarrer tracking nouvelle question

  }

  get currentQuestion(): TestQuestion | null {
    if (!this.session) return null;
    return this.session.questions[this.session.currentQuestionIndex] || null;
  }

  /**
   * Justification de la question courante, sur un test d'entraînement composé
   * par l'élève. `null` partout ailleurs (devoir, diagnostic, reprise après
   * rechargement) : le panneau disparaît alors, plutôt que d'afficher une
   * raison inventée.
   */
  get currentReason(): PracticeQuestion | null {
    const q = this.currentQuestion;
    if (!q || !this.testId) return null;
    return this.practiceContext.reasonFor(this.testId, q.question.id);
  }

  /** Clé i18n de la raison, ex. `PRACTICE.REASON_GAP`. */
  reasonLabelKey(reason: string): string {
    return `PRACTICE.REASON_${reason.toUpperCase()}`;
  }

  get progressValue(): number {
    if (!this.session) return 0;
    return (
      ((this.session.currentQuestionIndex + 1) /
        this.session.questions.length) *
      100
    );
  }

  get answeredCount(): number {
    return this.session?.answers.size || 0;
  }

  get totalQuestions(): number {
    return this.session?.questions.length || 0;
  }

  get isFirstQuestion(): boolean {
    return this.session?.currentQuestionIndex === 0;
  }

  get isLastQuestion(): boolean {
    if (!this.session) return false;
    return (
      this.session.currentQuestionIndex === this.session.questions.length - 1
    );
  }

  /** Au-delà d'une page, le navigateur se pagine ; en dessous, il se donne en entier. */
  get navigateurPagine(): boolean {
    return this.totalQuestions > this.bullesParPage;
  }

  get nombreDePages(): number {
    return Math.max(1, Math.ceil(this.totalQuestions / this.bullesParPage));
  }

  /**
   * Les rangs des questions de la page affichée.
   *
   * <p>Des rangs et non des questions : le gabarit indexe déjà
   * <c>session.questions</c>, et une liste d'objets recréés à chaque cycle de
   * détection ferait reconstruire les vingt boutons à chaque battement du
   * minuteur.</p>
   */
  get indicesAffiches(): number[] {
    const total = this.totalQuestions;
    if (!this.navigateurPagine) {
      return Array.from({ length: total }, (_, i) => i);
    }

    const debut = this.pageNavigateur * this.bullesParPage;
    const fin = Math.min(debut + this.bullesParPage, total);
    return Array.from({ length: Math.max(fin - debut, 0) }, (_, k) => debut + k);
  }

  /** Suivi d'identité par le rang : le bouton de la question 37 reste le même. */
  parIndice = (_: number, indice: number): number => indice;

  /** Premier et dernier rangs affichés, tels qu'on les lit (à partir de 1). */
  get premiereBulleAffichee(): number {
    return this.indicesAffiches.length ? this.indicesAffiches[0] + 1 : 0;
  }

  get derniereBulleAffichee(): number {
    const indices = this.indicesAffiches;
    return indices.length ? indices[indices.length - 1] + 1 : 0;
  }

  /** La question de ce rang a-t-elle une réponse ? */
  estRepondue(indice: number): boolean {
    const question = this.session?.questions[indice];
    return !!question && !!this.session?.answers.has(question.question.id);
  }

  pagePrecedente(): void {
    if (this.pageNavigateur > 0) this.pageNavigateur--;
  }

  pageSuivante(): void {
    if (this.pageNavigateur < this.nombreDePages - 1) this.pageNavigateur++;
  }

  /**
   * Ramène la page sur celle qui contient la question courante.
   *
   * <p>Appelé à chaque changement de question, et à l'ouverture : une copie
   * reprise à la trentième question doit s'ouvrir sur la page de la
   * trentième, pas sur la première.</p>
   *
   * <p>L'élève peut feuilleter librement entre deux questions — rien ne le
   * ramène tant qu'il ne change pas de question.</p>
   */
  private calerPageSurQuestionCourante(): void {
    const index = this.session?.currentQuestionIndex ?? 0;
    this.pageNavigateur = Math.min(
      Math.floor(index / this.bullesParPage),
      this.nombreDePages - 1,
    );
  }

  /**
   * Toutes les questions sont traitées : répondues, ou expirées — une question
   * dont le temps est passé ne peut plus l'être, elle ne retient donc pas la
   * copie.
   */
  get toutTraite(): boolean {
    const session = this.session;
    if (!session || session.questions.length === 0) return false;

    return session.questions.every(
      (q, i) =>
        (session.answers.get(q.question.id)?.length ?? 0) > 0 ||
        session.timedOutQuestions.has(i),
    );
  }

  /**
   * La remise est-elle proposée à l'élève ?
   *
   * <p><b>Pourquoi ce n'est plus « être à la dernière question ».</b> Le bouton
   * de remise était le bouton de la dernière question. Sur un diagnostic de
   * 52 questions, un élève qui avait tout répondu puis était revenu vérifier
   * la 30ᵉ n'avait plus aucun moyen de rendre sa copie : il lui fallait
   * retraverser vingt-deux questions pour retrouver le bouton, sans que rien
   * ne le lui dise.</p>
   *
   * <p>Une copie terminée se rend d'où l'on est. La dernière question garde son
   * bouton même incomplète — <c>submitTest</c> avertit alors du nombre de
   * questions sans réponse, il n'empêche pas.</p>
   */
  get peutRemettre(): boolean {
    return this.isLastQuestion || this.toutTraite;
  }

  /**
   * Fait suivre le repère à la question courante : la bonne page, puis la
   * bulle dans le cadre.
   *
   * <p>Le navigateur reste borné en hauteur après pagination — vingt bulles
   * tiennent en trois rangées sur un téléphone, mais pas sur un écran de
   * 320 px. Le défilement interne est le filet.</p>
   */
  private suivreLaQuestionCourante(): void {
    this.calerPageSurQuestionCourante();

    // Après le rendu : la classe qui désigne la bulle courante n'est posée
    // qu'au cycle de détection suivant.
    setTimeout(() => {
      this.navigateur?.nativeElement
        .querySelector<HTMLElement>('.question-nav-btn.btn-primary')
        ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }

  get isCurrentQuestionAnswered(): boolean {
    if (!this.session || !this.currentQuestion) return false;
    const currentQuestionId = this.currentQuestion.question.id;
    const answers = this.session.answers.get(currentQuestionId);
    return answers !== undefined && answers.length > 0;
  }

  get isCurrentQuestionTimedOut(): boolean {
    if (!this.session) return false;
    return this.session.timedOutQuestions.has(
      this.session.currentQuestionIndex,
    );
  }

  get isNextButtonDisabled(): boolean {
    // Disable next if question not answered and not timed out
    return !this.isCurrentQuestionAnswered && !this.isCurrentQuestionTimedOut;
  }

  canNavigateToQuestion(index: number): boolean {
    if (!this.session) return false;

    // Can't go back to timed out questions
    if (this.session.timedOutQuestions.has(index)) {
      return false;
    }

    // Can navigate to any previously visited question
    if (this.session.visitedQuestions.has(index)) {
      return true;
    }

    // Can navigate to next question if current is answered or timed out
    if (index === this.session.currentQuestionIndex + 1) {
      return this.isCurrentQuestionAnswered || this.isCurrentQuestionTimedOut;
    }

    return false;
  }

  selectAnswer(questionId: number, answerIds: number[]): void {
    if (!this.session) return;

    // Don't allow answering timed out questions
    if (this.isCurrentQuestionTimedOut) {
      this.swal.warning(
        this.translate.instant('TESTS.CANNOT_ANSWER_TIMED_OUT'),
      );
      return;
    }

    this.session.answers.set(questionId, answerIds);
    console.log('📝 Answer selected:', {
      questionId,
      answerIds,
      totalAnswered: this.session.answers.size,
      totalQuestions: this.session.questions.length,
    });

    // Marquée à envoyer avant la sauvegarde locale : si l'envoi échoue, la
    // mémoire du navigateur sait qu'elle reste à transmettre.
    this.enregistrerReponse(questionId);
    this.saveCurrentState();
  }

  /**
   * Au plus un envoi par fenêtre. Une requête par clic multipliait le trafic
   * d'une passation, face à un anti-flood qui compte 100 requêtes par minute
   * et par adresse IP — celle d'une classe entière derrière sa box.
   */
  private delaiEnregistrementMs = 20_000;

  /**
   * Vrai quand le dernier envoi a échoué et que des réponses attendent encore.
   *
   * <p>L'échec était muet : un `console.warn` ne s'adresse à personne. Un élève
   * a composé 41 minutes sur 52 questions en croyant être enregistré ; la base
   * n'en a gardé que deux, et il ne l'a su qu'en ne pouvant pas remettre.</p>
   *
   * <p>Le bandeau ne s'affiche donc pas pour rassurer : il s'affiche pour qu'un
   * élève sache, pendant l'épreuve, que sa copie ne part pas — et qu'il puisse
   * appeler son surveillant au lieu de découvrir la perte à la fin.</p>
   */
  enregistrementEnEchec = false;

  /**
   * Vrai une fois le composant détruit.
   *
   * <p>La reprise reprogramme un envoi à chaque échec. Sans cette borne, un
   * écran quitté pendant une panne réseau continuerait de réessayer
   * indéfiniment, après que plus personne ne le regarde.</p>
   */
  private detruit = false;

  /**
   * Reprises automatiques enchaînées depuis le dernier envoi réussi.
   *
   * <p>Borner est nécessaire, pas prudent : contre un serveur qui refuse
   * définitivement — une échéance dépassée, par exemple — réessayer sans fin
   * n'enregistrera jamais rien et occupe la page indéfiniment.</p>
   *
   * <p>Au-delà, le bandeau reste affiché : il dit la vérité, la copie n'est
   * pas enregistrée. Et la prochaine réponse de l'élève relance le cycle,
   * puisqu'elle programme un envoi.</p>
   */
  private reprisesConsecutives = 0;

  private readonly reprisesMax = 5;

  /** Nombre de questions que le serveur n'a pas confirmées. */
  get reponsesNonEnregistrees(): number {
    return this.nonSynchronisees.size;
  }

  /** Questions modifiées dont le serveur n'a pas confirmé la réception. */
  private nonSynchronisees = new Set<number>();

  /** Version de chaque réponse : un envoi ne confirme que ce qu'il a porté. */
  private versions = new Map<number, number>();

  private minuterieEnregistrement?: ReturnType<typeof setTimeout>;

  /** File des envois : ils partent l'un après l'autre, jamais croisés. */
  private enregistrements: Promise<void> = Promise.resolve();

  /**
   * Note qu'une réponse est à transmettre au serveur.
   *
   * <p>La copie ne vivait que dans ce navigateur jusqu'à la remise : le
   * service savait enregistrer en cours de test, personne ne l'appelait.</p>
   */
  private enregistrerReponse(questionId: number): void {
    this.reprisesConsecutives = 0;
    this.nonSynchronisees.add(questionId);
    this.versions.set(questionId, (this.versions.get(questionId) ?? 0) + 1);
    this.planifierEnregistrement();
  }

  /**
   * Note — ou retire — le doute sur la question courante.
   *
   * <p>Rien ne part au serveur : c'est une aide à la navigation, pas une
   * réponse. La marque vit dans ce navigateur et dans l'état local.</p>
   */
  basculerMarque(): void {
    const id = this.currentQuestion?.question?.id;
    if (id == null) return;

    this.marquees.has(id) ? this.marquees.delete(id) : this.marquees.add(id);
    if (!this.isSubmitting && !this.isExiting) this.saveCurrentState();
  }

  private planifierEnregistrement(): void {
    if (this.minuterieEnregistrement !== undefined) return;
    this.minuterieEnregistrement = setTimeout(() => {
      this.minuterieEnregistrement = undefined;
      this.envoyerNonSynchronisees();
    }, this.delaiEnregistrementMs);
  }

  /**
   * Envoie, en une requête, l'état actuel de toutes les questions à
   * transmettre.
   *
   * <p>Un échec n'interrompt pas l'élève : la question reste à transmettre,
   * repart au prochain envoi et, de toute façon, avec la copie complète à la
   * remise. Une question entièrement décochée s'envoie vide : le serveur
   * efface alors l'ancienne sélection au lieu de la garder — et de la noter.</p>
   */
  private envoyerNonSynchronisees(): void {
    if (!this.sessionId) return;
    const sessionId = this.sessionId;

    this.enregistrements = this.enregistrements.then(async () => {
      if (!this.session || this.nonSynchronisees.size === 0) return;
      const answers = this.session.answers;
      // Le temps passé part avec les réponses : repris sur un autre appareil,
      // le compteur ne repart pas de zéro.
      const tempsPasse = Math.round(this.session.tempsActif);

      const envoi = Array.from(this.nonSynchronisees, (questionId) => ({
        questionId,
        version: this.versions.get(questionId) ?? 0,
      }));
      const lot = envoi.flatMap(({ questionId }): SubmitAnswerItemDto[] => {
        const answerIds = answers.get(questionId) ?? [];
        return answerIds.length
          ? answerIds.map((answerId) => ({ questionId, answerId }))
          : [{ questionId, answerId: null }];
      });

      try {
        await firstValueFrom(
          this.studentTestService.saveAnswers(sessionId, lot, tempsPasse).pipe(timeout(15_000)),
        );
        // Seules les réponses inchangées depuis le départ sont confirmées.
        for (const { questionId, version } of envoi) {
          if ((this.versions.get(questionId) ?? 0) === version) {
            this.nonSynchronisees.delete(questionId);
          }
        }
        this.enregistrementEnEchec = false;
        this.reprisesConsecutives = 0;
        if (!this.isSubmitting && !this.isExiting) this.saveCurrentState();
      } catch (erreur) {
        console.warn('💾 Réponses gardées localement, non enregistrées sur le serveur :', erreur);

        // Dit à l'élève, et retenté tout seul.
        //
        // Un envoi n'était programmé QUE par une nouvelle réponse : l'élève
        // arrivé à la dernière question n'avait plus aucune occasion de
        // retenter, et sa copie restait dans son navigateur. La reprise suit la
        // même fenêtre de vingt secondes — elle ne multiplie donc pas le trafic
        // face à l'anti-flood.
        this.enregistrementEnEchec = this.nonSynchronisees.size > 0;

        if (this.enregistrementEnEchec
            && !this.detruit && !this.isSubmitting && !this.isExiting
            && ++this.reprisesConsecutives <= this.reprisesMax)
          this.planifierEnregistrement();
      }
    });
  }

  goToNextQuestion(): void {
    if (!this.session || this.isLastQuestion) return;

    if (!this.isCurrentQuestionAnswered && !this.isCurrentQuestionTimedOut) {
      this.swal.warning(
        this.translate.instant('TESTS.ANSWER_REQUIRED_MESSAGE'),
      );
      return;
    }

    this.moveToNextQuestion();
  }

  goToPreviousQuestion(): void {
    this.stopQuestionTracking();
    if (!this.session || this.isFirstQuestion) return;

    // Find previous accessible question
    let targetIndex = this.session.currentQuestionIndex - 1;

    // Skip timed out questions when going backwards
    while (
      targetIndex >= 0 &&
      this.session.timedOutQuestions.has(targetIndex)
    ) {
      targetIndex--;
    }

    if (targetIndex < 0) {
      this.swal.warning(this.translate.instant('TESTS.NO_PREVIOUS_QUESTION'));
      return;
    }

    this.session.currentQuestionIndex = targetIndex;

    const prevQuestion = this.currentQuestion;
    if (prevQuestion?.question.timeLimitSeconds) {
      this.session.questionTimeRemaining =
        prevQuestion.question.timeLimitSeconds;
    }
this.startQuestionTracking();
    this.saveCurrentState();
    this.suivreLaQuestionCourante();
  }

  jumpToQuestion(index: number): void {
    this.stopQuestionTracking();
    if (!this.session || index < 0 || index >= this.session.questions.length)
      return;

    if (!this.canNavigateToQuestion(index)) {
      const isTimedOut = this.session.timedOutQuestions.has(index);
      const message = isTimedOut
        ? this.translate.instant('TESTS.QUESTION_TIMED_OUT')
        : this.translate.instant('TESTS.NAVIGATION_RESTRICTED');

      this.swal.warning(message);
      return;
    }

    this.session.currentQuestionIndex = index;
    // Le repère a servi : on rend la place à l'énoncé.
    this.repereOuvert = false;

    if (index > this.session.highestQuestionReached) {
      this.session.highestQuestionReached = index;
    }

    this.session.visitedQuestions.add(index);

    const question = this.currentQuestion;
    if (question?.question.timeLimitSeconds) {
      this.session.questionTimeRemaining = question.question.timeLimitSeconds;
    }
this.startQuestionTracking();
    this.saveCurrentState();
    this.suivreLaQuestionCourante();
  }

  /**
   * Get tooltip title for question navigation buttons
   */
  getQuestionButtonTitle(index: number): string {
    if (!this.session) return '';

    const questionNum = index + 1;

    if (index === this.session.currentQuestionIndex) {
      return this.translate.instant('TESTS.CURRENT_QUESTION', {
        num: questionNum,
      });
    }

    if (this.session.timedOutQuestions.has(index)) {
      return this.translate.instant('TESTS.QUESTION_TIMED_OUT_TOOLTIP', {
        num: questionNum,
      });
    }

    if (this.session.answers.has(this.session.questions[index].question.id)) {
      return this.translate.instant('TESTS.QUESTION_ANSWERED', {
        num: questionNum,
      });
    }

    if (!this.canNavigateToQuestion(index)) {
      return this.translate.instant('TESTS.QUESTION_LOCKED_TOOLTIP', {
        num: questionNum,
      });
    }

    return this.translate.instant('TESTS.GO_TO_QUESTION', { num: questionNum });
  }

  async submitTest(autoSubmit: boolean = false): Promise<void> {
    if (!this.session || this.isSubmitting) return;

    const unansweredCount = this.totalQuestions - this.answeredCount;

    if (!autoSubmit && unansweredCount > 0) {
      const confirmSubmit = await this.swal.confirmAsync(
        this.translate.instant('TESTS.UNANSWERED_QUESTIONS_MESSAGE', {
          count: unansweredCount,
        }),
      );

      if (!confirmSubmit) return;
    }

    // Les doutes notés reviennent AVANT la confirmation : les rappeler après
    // aurait obligé à annuler pour y retourner. Ils avertissent, ils
    // n'empêchent pas — décision du 25/09/2026 : un élève qui veut remettre
    // avec trois doutes en a le droit, il doit juste le savoir.
    if (!autoSubmit && this.marquees.size > 0) {
      const poursuivre = await this.swal.confirmAsync(
        this.translate.instant('TESTS.MARKED_QUESTIONS_WARNING', {
          count: this.marquees.size,
        }),
      );

      if (!poursuivre) return;
    }

    if (!autoSubmit) {
      const confirmed = await this.swal.confirmAsync(
        this.translate.instant('TESTS.CONFIRM_SUBMIT_MESSAGE'),
      );

      if (!confirmed) return;
    }

    // Mark as submitting to prevent state save
    this.isSubmitting = true;
    this.endExamSession();

    try {
      this.stopQuestionTracking();

      // Le temps réellement passé sur le test, et non « maintenant − heure de
      // début » : ce calcul comptait les heures où l'écran était fermé, et une
      // copie remise à la fin du temps affichait toujours la durée du test.
      const timeSpentSeconds = Math.round(this.session.tempsActif);

      // Build answers: one SubmitAnswerItemDto per selected answerId,
      // with timeSpentSeconds on the first entry for each question.
      // Les questions sans réponse ne sont pas envoyées : une entrée à
      // answerId null fait rejeter le lot entier par le back (400).
      const answers: SubmitAnswerItemDto[] = [];
      for (const [questionId, answerIds] of this.session.answers.entries()) {
        const timeSpent = Math.round(this.questionTimeSpent.get(questionId) ?? 0);
        answerIds.forEach((answerId, idx) => {
          answers.push({
            questionId,
            answerId,
            timeSpentSeconds: idx === 0 ? timeSpent : undefined,
          });
        });
      }

      const metadata = {
        tabSwitchCount: this.tabSwitchCount,
        focusLossCount: this.focusLossCount,
        fullscreenExitCount: this.fullscreenExitCount,
        pageVisibilityChanges: this.tabSwitchCount,
      };

      console.log('📤 Submitting test to backend:', {
        sessionId: this.sessionId,
        answersCount: answers.length,
        timeSpentSeconds,
        metadata,
      });

      // Clear saved state immediately before the async call
      if (this.testId) {
        this.timerService.clearTestState(this.testId);
      }

      if (!this.sessionId) {
        throw new Error('No active backend session. Please restart the test.');
      }

      // Le back exige au moins une réponse : sans rien à envoyer, inutile
      // d'appeler (400 garanti). On informe et on sort de la session.
      if (answers.length === 0) {
        this.exitBrowserFullscreen();
        await this.swal.warning(
          this.translate.instant('TESTS.NO_ANSWERS_TO_SUBMIT'),
        );
        this.router.navigate(['/student/tests']);
        return;
      }

      // La remise porte toute la copie : l'envoi en attente devient inutile.
      // Celui déjà parti ne doit pas la croiser (il est borné à 15 s).
      clearTimeout(this.minuterieEnregistrement);
      this.minuterieEnregistrement = undefined;
      await this.enregistrements;

      // ── Send to backend ─────────────────────────────────────────────────
      const result = await firstValueFrom(
        this.studentTestService.completeTestSession(this.sessionId, {
          answers,
          timeSpentSeconds,
          metadata,
        }),
      );

      if (result.status !== 'SUCCESS' || !result.data) {
        throw new Error('Server returned an error while completing the session');
      }

      const scoreResult = result.data;
      console.log('✅ Test completed, score:', scoreResult);
      this.exitBrowserFullscreen();

      if (this.session.test.showResultsImmediately) {
        // La copie se désigne par sa session, jamais par le test : deux
        // tentatives du même test partageaient sinon la même adresse, et un
        // rechargement de la page ne savait pas laquelle relire. L'état reste
        // passé — il évite un aller-retour serveur — mais il n'est plus que
        // du confort : l'écran sait se reconstruire sans lui.
        this.router.navigate(['/student/test/results', this.sessionId], {
          state: {
            scoreResult,
            sessionId: this.sessionId,
            answers: Array.from(this.session.answers.entries()),
            test: this.session.test,
            timeSpent: timeSpentSeconds,
            autoSubmitted: autoSubmit,
            timedOutQuestions: Array.from(this.session.timedOutQuestions),
            behavior: {
              tabSwitchCount: this.tabSwitchCount,
              focusLossCount: this.focusLossCount,
              fullscreenExitCount: this.fullscreenExitCount,
            },
            questionTimes: Array.from(this.questionTimeSpent.entries()),
          },
        });
      } else {
        await this.swal.success(this.translate.instant('TESTS.TEST_SUBMITTED'));
        this.router.navigate(['/student/tests']);
      }
    } catch (error) {
      console.error('❌ Error submitting test:', error);
      this.isSubmitting = false;
      // La copie reste ouverte : ce qui n'a pas été transmis repart, sans
      // attendre le prochain clic.
      if (this.nonSynchronisees.size > 0) this.planifierEnregistrement();
      this.swal.error(this.translate.instant('ERRORS.SUBMIT_TEST_ERROR'));
    }
  }

    // ===== Question Time Tracking =====
  private startQuestionTracking(): void {
    if (!this.currentQuestion) return;
    this.questionStartTimestamp = Date.now();
  }

  private stopQuestionTracking(): void {
    if (!this.currentQuestion) return;

    const questionId = this.currentQuestion.question.id;
    const timeSpent = (Date.now() - this.questionStartTimestamp) / 1000;

    const previous = this.questionTimeSpent.get(questionId) || 0;
    this.questionTimeSpent.set(questionId, previous + timeSpent);

    console.log("⏱️ Time spent:", {
      questionId,
      seconds: this.questionTimeSpent.get(questionId)
    });
  }

  private handleError(error: unknown): void {
    this.isLoading = false;
    this.hasError = true;
    // Jamais le texte technique (« Http failure response … 403 OK ») : la
    // raison, dans la langue de l'élève. Le détail reste dans la console.
    this.errorMessage = this.translate.instant(cleRefusDemarrage(error));
    this.swal.error(this.errorMessage);
  }

  async exitTest(): Promise<void> {
    const confirmed = await this.swal.confirmAsync(
      this.translate.instant('TESTS.EXIT_TEST_MESSAGE'),
    );

    if (confirmed) {
      this.isExiting = true;
      this.endExamSession();
      this.exitBrowserFullscreen();

      // Clear the saved state completely
      if (this.testId) {
        this.timerService.clearTestState(this.testId);
      }

      console.log('🚪 User exited test intentionally');
      this.router.navigate(['/student/tests']);
    }
  }
private saveBehavior(): void {
  sessionStorage.setItem('examBehavior', JSON.stringify({
    tabSwitchCount: this.tabSwitchCount,
    focusLossCount: this.focusLossCount,
    fullscreenExitCount: this.fullscreenExitCount,
  }));
}

private lockBodyScroll(): void {
  document.body.style.overflow = 'hidden';
}

private unlockBodyScroll(): void {
  document.body.style.overflow = '';
}

/** Résolue une fois le plein écran obtenu — ou refusé, sans conséquence. */
private enterFullScreen(): Promise<void> {
  return (document.documentElement.requestFullscreen?.() ?? Promise.resolve())
    .then(() => console.log('🖥️ Browser fullscreen activated'))
    .catch(err => console.warn('Fullscreen unavailable:', err));
}

private exitBrowserFullscreen(): void {
  if (document.fullscreenElement) {
    document.exitFullscreen?.().catch(() => {});
  }
}

private attachAntiCheatListeners(): void {
  document.addEventListener('fullscreenchange', this.onFullscreenChange);
  document.addEventListener('visibilitychange', this.onVisibilityChange);
  window.addEventListener('blur', this.onWindowBlur);
}

private detachAntiCheatListeners(): void {
  document.removeEventListener('fullscreenchange', this.onFullscreenChange);
  document.removeEventListener('visibilitychange', this.onVisibilityChange);
  window.removeEventListener('blur', this.onWindowBlur);
}

private endExamSession(): void {
  if (!this.examSessionService.isExamActive) return;
  this.examSessionService.endExam();
  this.detachAntiCheatListeners();
}

  ngOnDestroy(): void {
    // Safety net: always end session regardless of how the component is destroyed
    this.endExamSession();
    this.unlockBodyScroll();
    this.exitBrowserFullscreen();

    this.detruit = true;

    // Clear all timers
    if (this.testTimerSubscription) {
      this.testTimerSubscription.unsubscribe();
    }
    if (this.questionTimerSubscription) {
      this.questionTimerSubscription.unsubscribe();
    }
    if (this.saveStateInterval) {
      clearInterval(this.saveStateInterval);
    }

    // Sortie par la navigation, en plein test : ce qui attendait la fenêtre
    // part tout de suite, pour une reprise ailleurs.
    clearTimeout(this.minuterieEnregistrement);
    this.minuterieEnregistrement = undefined;
    if (!this.isSubmitting && !this.isExiting) this.envoyerNonSynchronisees();

    // Only save state if refreshing (not submitting or exiting)
    if (!this.isSubmitting && !this.isExiting && this.session) {
      this.saveCurrentState();
      console.log('💾 State saved on component destroy (refresh)');
    } else if (this.testId) {
      this.timerService.clearTestState(this.testId);
      console.log('🗑️ State cleared on component destroy (exit/submit)');
    }

    this.destroy$.next();
    this.destroy$.complete();

    console.log('🧹 Component destroyed');
  }
}
